import React, { useRef, useState, useCallback } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
    intl,
    DmsAltBox,
    DmsPage,
    DmsTable,
    DmsButton,
    DmsPermissions,
    fetchData,
} from "dmsbasereact";
import Swal from "sweetalert2";
import { Typography, IconButton, Tooltip, Box } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { formatCnpj } from "../../utils/formatters";
import { APP_ROUTES } from "../../utils/NamedRoutes";
import { Permissions } from "../../constants/Permissions";
import { PartnerV2LinkModal } from "../../components/PartnerV2LinkModal";

export const PartnerV2DetailPage = () => {
    const table = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();
    const { distributorPk } = useParams();

    const [modalOpen, setModalOpen] = useState(false);

    const distributorName = location.state?.distributorName || intl.get("Distributor").d("Distributor");
    const distributorCnpj = location.state?.distributorCnpj || "";
    const distributorIsPublic = location.state?.distributorIsPublic || false;

    const handleGoBack = () => {
        navigate(APP_ROUTES.ReportsDistributorClients);
    };

    const handleOpenModal = useCallback(() => {
        setModalOpen(true);
    }, []);

    const handleCloseModal = useCallback(() => {
        setModalOpen(false);
    }, []);

    const handleSave = useCallback(() => {
        if (table.current) {
            table.current.updateTable();
        }
    }, []);

    const handleUnlink = useCallback(async (row) => {
        const result = await Swal.fire({
            icon: "warning",
            title: intl.get("ConfirmUnlinkPartner").d("Are you sure you want to unlink this partner?"),
            showCancelButton: true,
            confirmButtonText: intl.get("Yes").d("Yes"),
            cancelButtonText: intl.get("No").d("No"),
        });

        if (result.isConfirmed) {
            try {
                await fetchData(
                    `/partner/v2/link/${distributorPk}/${row.organization_branch_pk}`,
                    "DELETE",
                    true
                );
                Swal.fire({
                    icon: "success",
                    title: intl.get("PartnerUnlinkedSuccessfully").d("Partner unlinked successfully"),
                });
                if (table.current) {
                    table.current.updateTable();
                }
            } catch {
                Swal.fire({
                    icon: "error",
                    title: intl.get("FailedToUnlinkPartner").d("Failed to unlink partner"),
                });
            }
        }
    }, [distributorPk]);

    const header = [
        {
            label: intl.get("InstitutionName").d("Institution Name"),
            column_name: "name",
            filter: true,
        },
        {
            label: intl.get("CNPJ").d("CNPJ"),
            column_name: "organization_branch",
            sortable: false,
            function: (value) => formatCnpj(value?.cnpj),
        },
        {
            label: intl.get("Type").d("Type"),
            column_name: "organization_branch",
            sortable: false,
            function: (value) => value?.category_labels?.join(", ") || value?.category_label || "-",
        },
        {
            label: intl.get("Address").d("Address"),
            column_name: "organization_branch",
            sortable: false,
            function: (value) => {
                const addr = value?.address;
                if (!addr) return "-";
                return [addr.address, addr.number].filter(Boolean).join(", ");
            },
        },
        {
            label: intl.get("ZipCode").d("Zip Code"),
            column_name: "organization_branch",
            sortable: false,
            function: (value) => value?.address?.zip_code || "-",
        },
        {
            label: intl.get("City").d("City"),
            column_name: "organization_branch",
            sortable: false,
            function: (value) => value?.address?.city || "-",
        },
        {
            label: intl.get("State").d("State"),
            column_name: "organization_branch",
            sortable: false,
            width: "80px",
            function: (value) => value?.address?.state || "-",
        },
        {
            label: intl.get("Actions").d("Actions"),
            column_name: "actions",
            sortable: false,
            width: "80px",
            element: (row) => (
                <DmsPermissions permissions={[Permissions.PARTNER_LINK_DELETE]}>
                    <Tooltip title={intl.get("Unlink").d("Unlink")}>
                        <IconButton
                            size="small"
                            onClick={() => handleUnlink(row)}
                            color="error"
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                </DmsPermissions>
            ),
        },
    ];

    return (
        <DmsPage name="PartnerV2Detail" message={intl.get("PartnerV2DetailPage.PageAccess", { name: distributorName }).d(`User accessed Partners by Distributor: ${distributorName}`)}>

            <DmsAltBox.Card>
                <DmsAltBox.Header>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Tooltip title={intl.get("Back").d("Back")}>
                            <IconButton
                                size="small"
                                onClick={handleGoBack}
                                sx={{
                                    color: "primary.main",
                                    backgroundColor: "#fff",
                                    "&:hover": { backgroundColor: "grey.100" },
                                }}
                            >
                                <ArrowBackIcon />
                            </IconButton>
                        </Tooltip>
                        <Box>
                            <Typography variant="h3" sx={{ fontSize: "1.1rem" }}>
                                {distributorName}
                            </Typography>
                            {distributorCnpj && (
                                <Typography variant="caption" color="text.secondary">
                                    {formatCnpj(distributorCnpj)}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </DmsAltBox.Header>
                <DmsAltBox.Body>
                    <DmsPermissions permissions={[Permissions.PARTNER_LINK_CREATE]}>
                        <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 2 }}>
                            <DmsButton
                                icon={<AddIcon />}
                                text={intl.get("AddPartners").d("Add Partners")}
                                onClick={handleOpenModal}
                                variant="primary"
                            />
                        </Box>
                    </DmsPermissions>
                    <DmsTable
                        header={header}
                        managed={true}
                        needs_authentication={true}
                        ref={table}
                        url={`/partner/v2/${distributorPk}/institutions/table`}
                        export_table={true}
                    />
                </DmsAltBox.Body>
            </DmsAltBox.Card>

            <PartnerV2LinkModal
                isVisible={modalOpen}
                onClose={handleCloseModal}
                onSave={handleSave}
                distributorPk={Number(distributorPk)}
                distributorName={distributorName}
                distributorIsPublic={distributorIsPublic}
            />
        </DmsPage>
    );
};

export default PartnerV2DetailPage;
